import {
  MessageSquareText,
  HelpCircle,
  AlignLeft,
  GitCompareArrows,
  Lightbulb,
  Flag,
} from 'lucide-react';

import Container from '../../Main/sections/Container';
import Reveal from '../../../components/Reveal';

const ABILITIES = [
  {
    icon: MessageSquareText,
    title: 'Explain Any Concept',
    body: 'Ask Andy to break down any term or process from your notes. He adjusts the depth automatically — simple overview or detailed walkthrough, your call.',
    quote: '"Andy, explain the difference between mitosis and meiosis like I\'m 10."',
  },
  {
    icon: HelpCircle,
    title: 'Quiz You On the Spot',
    body: 'Ask Andy to test you verbally on any topic. He asks questions, waits for your answer, then gives feedback — like a real oral exam practice session.',
    quote: '"Andy, quiz me on the four phases of mitosis."',
  },
  {
    icon: AlignLeft,
    title: 'Summarise a Topic',
    body: 'Ask Andy for a clean, concise summary of any section — perfect for quick revision before a lecture or when you need a refresher in 30 seconds.',
    quote: '"Andy, give me a 3-bullet summary of cell cycle checkpoints."',
  },
  {
    icon: GitCompareArrows,
    title: 'Compare & Contrast',
    body: 'Andy excels at side-by-side comparisons — two processes, two theories, two dates. He pulls from your notes and presents it as a clear comparison table.',
    quote: '"Andy, compare prophase and metaphase in a table."',
  },
  {
    icon: Lightbulb,
    title: 'Give Real Examples',
    body: "Abstract concepts get concrete instantly. Tell Andy what context you understand best — sport, cooking, music — and he'll translate your biology into that world.",
    quote: '"Andy, explain DNA replication using a cooking analogy."',
  },
  {
    icon: Flag,
    title: 'Flag What You Might Miss',
    body: "Ask Andy what topics your notes cover that are likely to appear in exams, or what common mistakes students make on this subject. He's read enough to know.",
    quote: '"Andy, what are the most likely exam questions from my notes?"',
  },
];

const Abilities = () => {
  return (
    <section className="relative py-20 lg:py-24">
      <div className="glow left-1/2 top-0 h-[360px] w-[600px] -translate-x-1/2 bg-accent/8" />
      <Container className="relative">
        <Reveal className="text-center">
          <span className="eyebrow">Andy&apos;s Abilities</span>
          <h2 className="font-heading mx-auto mt-5 max-w-4xl text-[2rem] font-bold leading-[1.1] sm:text-[2.6rem] lg:text-[3rem]">
            <span className="head-fade">Not Just Q&amp;A — </span>
            <span className="text-accent-grad">A Full Teaching Toolkit</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-[0.95rem] text-white/55">
            Andy is built to do everything a great human tutor would do — explain,
            quiz, simplify, summarise, compare, and motivate.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {ABILITIES.map((a, i) => (
            <Reveal key={a.title} delay={(i % 3) * 90}>
              <div className="glass glass-hover flex h-full flex-col rounded-2xl p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/12 text-accent">
                  <a.icon size={21} />
                </span>
                <h3 className="font-heading mt-5 text-[1.1rem] font-semibold text-white">
                  {a.title}
                </h3>
                <p className="mt-2 text-[0.87rem] leading-relaxed text-white/55">
                  {a.body}
                </p>
                <div className="mt-4 rounded-xl border border-accent/20 bg-accent/[0.06] px-3.5 py-2.5 text-[0.8rem] italic leading-relaxed text-accent/90">
                  {a.quote}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Abilities;
